// /* eslint-disable jsx-a11y/anchor-is-valid */
import { enIN } from 'date-fns/locale';
import { Field, Formik, Form as FormikForm, useField } from 'formik';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Button, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { registerLocale } from 'react-datepicker';
import validAllowedCharacters from '../../../constants/valid-character-password';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { ProductFilterInitialState } from '../../../core/initialState/filter-initial-state';
import { IProductFilter } from '../../../models/IProductFilter';
import FormikFormDatePicker from '../../../shared-components/FormikFormHandlers/FormikFormDatePicker';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { FilterValidationSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import {
  ClearFilterIcon,
  CloseFilterIcon,
  FilterIcon,
} from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';

registerLocale('en-IN', enIN);

interface IProps {
  initialValues: any;
  id: number;
  submitHandler: (data: any) => void;
  filterParmas: (data: any, isfilterCleard: boolean) => void;
  onModalClosed: () => void;
  isLoading: boolean;
  isDisable: boolean;
  isFilterCleared: boolean;
  isFilterApplied: boolean;
  setIsFilterApplied: (status: boolean) => void;
  setSearchTerm: (searchTerm: string) => void;
}

const SearchableInputField = ({
  name,
  label,
  options,
  placeholder = '',
  'data-testid': testId,
}: {
  name: string;
  label: string;
  options: string[];
  placeholder?: string;
  'data-testid'?: string;
}) => {
  const [field, meta, helpers] = useField(name);
  const [showOptions, setShowOptions] = useState(false);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes((field.value || '').toLowerCase())
  );

  return (
    <div className="form-group" style={{ position: 'relative' }}>
      <Label
        className="label-style col-form-label"
        required={false}
        label={label}
        htmlFor={name}
      />
      <input
        type="text"
        id={name}
        data-testid={testId || name}
        className="form-control"
        autoComplete="off"
        placeholder={placeholder}
        value={field.value || ''}
        onFocus={() => setShowOptions(true)}
        onBlur={() => setTimeout(() => setShowOptions(false), 200)}
        onChange={(e) => helpers.setValue(e.target.value)}
      />
      {showOptions && filteredOptions.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            border: '1px solid #ccc',
            borderRadius: '4px',
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            zIndex: 100,
            maxHeight: '150px',
            overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
          }}
        >
          {filteredOptions.map((opt, i) => (
            <li
              key={i}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '14px',
                color: '#333',
              }}
              onMouseDown={() => {
                helpers.setValue(opt);
                setShowOptions(false);
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FilterComponent = (props: IProps) => {
  const { userStore } = useStore();
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const tenantId = Number(getTenantIdFromLocalStorage());
    if (userStore && tenantId) {
      userStore.GetAllUserNameService(tenantId);
    }
  }, []);

  const userOptions: string[] = Array.from(
    new Set(
      (userStore?.allUserNameList || [])
        .map((u: any) => (u.UserName || '').trim())
        .filter((name: string) => name.length > 0)
    )
  );

  const handleToggle = (isOpen: boolean, resetForm: () => void) => {
    if (!isOpen) {
      resetForm();
    }
    setShowDropdown(isOpen);
  };

  function getLocalISODate(isoDate: Date) {
    return (
      isoDate.getFullYear() +
      '-' +
      String(isoDate.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(isoDate.getDate()).padStart(2, '0') +
      'T' +
      String(isoDate.getHours()).padStart(2, '0') +
      ':' +
      String(isoDate.getMinutes()).padStart(2, '0') +
      ':' +
      String(isoDate.getSeconds()).padStart(2, '0')
    );
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={props.initialValues}
        enableReinitialize
        validationSchema={FilterValidationSchema}
        validateOnBlur={false}
        onSubmit={async (
          values: IProductFilter,
          { resetForm, setFieldValue }
        ) => {
          let formattedValues = { ...values };
          if (values.CreatedDate) {
            const d = new Date(values.CreatedDate);
            if (!isNaN(d.getTime())) {
              formattedValues.CreatedDate = `${getLocalISODate(d).slice(0, 10)}`;
            }
          }
          if (values.UpdatedDate) {
            const d = new Date(values.UpdatedDate);
            if (!isNaN(d.getTime())) {
              formattedValues.UpdatedDate = `${getLocalISODate(d).slice(0, 10)}`;
            }
          }
          props.submitHandler(formattedValues);
          setShowDropdown(false);
          props.setIsFilterApplied(true);
        }}
      >
        {({ dirty, resetForm, setFieldValue, values }) => (
          <Dropdown
            className={`Filter-DID productFilters ${props.isDisable ? 'disabled-li' : ''}`}
            show={showDropdown}
            onToggle={(isOpen) => handleToggle(isOpen, resetForm)}
          >
            <Dropdown.Toggle
              className={`btn btn-light ${props.isFilterApplied ? 'btn-filter-disable' : 'btn-filter'}`}
              aria-expanded="false"
              title={formatMessage('filter')}
            >
              {!props.isFilterApplied ? (
                <FilterIcon isDisable={props.isDisable} />
              ) : (
                <ClearFilterIcon
                  onClick={(e) => {
                    resetForm();
                    setShowDropdown(!showDropdown);
                    props.setSearchTerm('');
                  }}
                />
              )}
            </Dropdown.Toggle>
            <FormikForm className="user-form">
              {props.isLoading ? (
                <FormLoader loading={props.isLoading} />
              ) : null}
              <Dropdown.Menu className="filter-menu">
                <Dropdown.ItemText className="p-0">
                  <Form.Group controlId="dateRange">
                    <div
                      className={`filter-header border-bottom p-3 px-4 ${props.isFilterCleared ? '' : ''}`}
                    >
                      <h4>
                        {formatMessage('filter')}
                        <a
                          className="clear-filter float-end"
                          onClick={() => {
                            resetForm();
                            setShowDropdown(false);
                          }}
                        >
                          <CloseFilterIcon/>
                        </a>
                      </h4>
                    </div>

                    <Row className="p-3 px-4">
                      <Col md={12}>
                        <div className="form-group">
                          <Label
                            className="label-style col-form-label"
                            required={false}
                            label="katashiki"
                            htmlFor="Katashiki"
                          />
                          <Field
                            data-testid="Name"
                            className="form-control"
                            name="Katashiki"
                            autoComplete="off"
                            placeholder=""
                            as={FormikFormInput}
                            validcharacter={validAllowedCharacters}
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <SearchableInputField
                          name="CreatedBy"
                          label="created_by"
                          data-testid="CreatedBy"
                          options={userOptions}
                        />
                      </Col>

                      <Col md={6}>
                        <div className="form-group">
                          <Label
                            className="label-style col-form-label"
                            required={false}
                            label="created_date"
                            htmlFor="CreatedDate"
                          />
                          <Field
                            data-testid="CreatedDate"
                            className="form-control"
                            name="CreatedDate"
                            autoComplete="off"
                            placeholder=""
                            as={FormikFormDatePicker}
                            isClearable={values.CreatedDate}
                            maxDate={values.UpdatedDate ? new Date(values.UpdatedDate) : undefined}
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <SearchableInputField
                          name="UpdatedBy"
                          label="updated_by"
                          data-testid="UpdatedBy"
                          options={userOptions}
                        />
                      </Col>

                      <Col md={6}>
                        <div className="form-group">
                          <Label
                            className="label-style col-form-label"
                            required={false}
                            label="updated_date"
                            htmlFor="UpdatedDate"
                          />
                          <Field
                            data-testid="UpdatedDate"
                            className="form-control"
                            name="UpdatedDate"
                            autoComplete="off"
                            placeholder=""
                            as={FormikFormDatePicker}
                            isClearable={values.UpdatedDate}
                            minDate={values.CreatedDate ? new Date(values.CreatedDate) : undefined}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Form.Group>
                </Dropdown.ItemText>
                <Dropdown.Divider className="mb-0" />
                <div className="d-flex filter-btn p-3 px-4 justify-content-between">
                  <div style={{ visibility: props.isFilterCleared ? 'hidden' : 'visible' }}>
                    <Button
                      className="clear-filter float-end"
                      onClick={() => {
                        props.filterParmas(ProductFilterInitialState, true);
                        resetForm();
                        setShowDropdown(false);
                        props.setIsFilterApplied(false);
                      }}
                    >
                      {formatMessage('clear_all_filters')}
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    type="submit"
                    className=""
                    disabled={
                      JSON.stringify(props.initialValues) === JSON.stringify(values) ||
                      (!values.Katashiki &&
                        !values.ProductId &&
                        !values.CreatedBy &&
                        !values.CreatedDate &&
                        !values.UpdatedBy &&
                        !values.UpdatedDate)
                    }
                  >
                    {formatMessage('apply')}
                  </Button>
                </div>
              </Dropdown.Menu>
            </FormikForm>
          </Dropdown>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default observer(FilterComponent);
